import React, { ReactNode, useRef, useState, useEffect, ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { styledTheme } from '../../common/themes';
import ProcedureTimeLine from '../../components/Consultation/features/procedure-view/timeline/ProcedureTimeLine';
import { StyledPrompt } from '../../components/Consultation/features/teeth-view/toolbar/dropdowns/SavePrompt';
import Header from '../../components/Header/Header';
import ImageUploader from '../../components/Images/ImageUploader';
import PdfPreviewExport, {
  PdfPreviewExportHandle,
} from '../../components/Shared/PdfPreviewExport/PdfPreviewExport';
import { Button } from '../../components/Shared/Buttons';
import { InputGroup, InputItem } from '../../components/Shared/Forms';
import { shareConsultation } from '../../redux/slices/apiSlice';
import { ProcedureStep, Visit } from '../../redux/slices/procedureSlice';
import { useAppDispatch, useAppSelector, useOnClickOutside } from '../../utils/hooks';
import styles from './ConsultationExportPage.module.scss';
import { updateClinic } from '../../redux/actions/clinicActions';
import { DownloadBlob } from '../../utils/nativeHelpers';
import styled from 'styled-components';
import ScaleContentToFit from '../../components/Shared/ScaleContentToFit/ScaleContentToFit';
import { SET_EXPORT_VIEW } from '../../redux/slices/UISlice';
import SavePrompt from '../../components/Consultation/features/teeth-view/toolbar/dropdowns/SavePrompt';
import { useParams } from 'react-router';
import EditableExportTitle from './EditableExportTitle';
import ReferralModal from '../../components/ReferralModal/ReferralModal';
import { SET_NOTE } from '../../redux/slices/procedureSlice';
import { updatePatient } from '../../redux/actions/patientActions';
import { appInsights } from '../../utils/applicationInsights';
import { ICurrentPatient } from '../../types/patient';
import { ICountry } from '../../types/country';
import { IClinic } from '../../types/clinic';
import { IUser } from '../../types/user';

const ConsultationExportPage = () => {
  const intl = useIntl();
  const { patientId, userGroupId } = useParams<Record<string, string | undefined>>();
  const patient = useAppSelector<ICurrentPatient>((state) => state.patient.currentPatient);
  const user = useAppSelector<IUser>((state) => state.user.user);
  const clinic = useAppSelector<IClinic>((state) => state.clinic.clinic);

  const referralNames = clinic.referrals
    ? clinic.referrals.map((referral) => {
        return { label: referral.clinician, value: referral.id };
      })
    : [];

  const [edit, setEdit] = useState<boolean>(true);
  const [download, setDownload] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showReferralModal, setShowReferralModal] = useState<boolean>(false);
  const [isEditingPdfData, setIsEditingPdfData] = useState<boolean>(false);
  const note = useAppSelector((state) => state.consultation.procedure.note);

  const patientFirstName = useAppSelector((state) => state.patient.currentPatient.firstName);
  const patientLastName = useAppSelector((state) => state.patient.currentPatient.lastName);

  const referringClinician = patient.referralId
    ? (clinic.referrals ?? []).filter(
        (referral: { id: string }) => referral.id === patient.referralId
      )[0]
    : undefined;
  const treatingDoctor =
    user.firstName || user.surname ? `${user.title}. ${user.firstName} ${user.surname}` : '';

  const [noteUnderEdit, setNoteUnderEdit] = useState(note);
  const [referringClinicianUnderEdit, setReferringClinicianUnderEdit] = useState({
    ...referringClinician,
  });

  useEffect(() => {
    setReferringClinicianUnderEdit({ ...referringClinician });
  }, [referringClinician]);

  const procedure = useAppSelector((state) => state.consultation.procedure);
  const countries = useAppSelector((state) => state.referenceData.countries);
  const clinicCountry =
    countries && countries.length > 0
      ? countries.find((country: ICountry) => country.key === clinic?.address?.countryCode)?.value
      : '';

  const dispatchTyped = useAppDispatch();
  const dispatch = useDispatch();

  const pdfRef = useRef<PdfPreviewExportHandle>(null);
  const promptRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(promptRef, () => setShowModal(false));

  const referralFooter = (
    <div data-cy="manage-referrals-div" onClick={() => setShowReferralModal(true)}>
      <FormattedMessage id="patientInformation.manageReferrals" defaultMessage="Manage referrals" />{' '}
      <i>draw</i>
    </div>
  );

  const getPairedStepsArray = (steps: ProcedureStep[]) => {
    const pairedSteps = steps.reduce(function (result: ProcedureStep[][], _value, index, array) {
      if (index % 2 === 0) result.push(array.slice(index, index + 2));
      return result;
    }, []);
    return pairedSteps;
  };

  const getStepHtml = (step: ProcedureStep, isFirstStep = false) => {
    return (
      <div className={styles.myStepWrapper}>
        <div className={styles.descriptionFlexChild}>
          <p>
            {intl.formatMessage({
              id: 'consultation.procedure.detail.description.label',
              defaultMessage: 'Description',
            }) + ':'}
          </p>
          <p>{step.description}</p>
        </div>
        <div className={`${styles.imageFlexChild} ${isFirstStep && styles.imageFlexChildFirst}`}>
          <img src={step.previewImageSrc} alt={'step view'} />
        </div>
      </div>
    );
  };

  const generatePDF = async (email: string) => {
    appInsights.trackEvent({
      name: 'PdfExport',
      properties: {
        type: 'consultation',
        email: user.email,
      },
    });
    setEdit(false);
    requestAnimationFrame(() => {
      if (pdfRef.current) {
        pdfRef.current.export((worker) => {
          const fileName = `Consultation ${patientFirstName} ${patientLastName} ${new Date().toLocaleDateString()}.pdf`;
          if (email) {
            worker.output('datauristring').then(function (pdfAsString: any) {
              dispatchTyped(
                shareConsultation({
                  email,
                  fileName,
                  file: pdfAsString.split(',')[1],
                })
              );
              setEdit(true);
              setShowModal(false);
            });
          } else {
            setDownload(true);
            worker.output('blob').then(function (blob: Blob) {
              DownloadBlob(blob, fileName);
              setEdit(true);
              setDownload(false);
            });
          }

          return worker;
        });
      }
    });
  };

  return (
    <ThemeProvider theme={styledTheme}>
      <SavePrompt patientId={patientId} userGroupId={userGroupId} />
      <div className={edit ? styles.edit : ''}>
        <ReferralModal show={showReferralModal} onHide={() => setShowReferralModal(false)} />
        <Header
          isTogglerHidden
          title={
            intl.formatMessage({
              id: 'consultationexport.Header',
              defaultMessage: 'CONSULTATION EXPORT',
            }) + `: ${patientFirstName} ${patientLastName}`
          }
          leftButtons={[
            {
              label: intl.formatMessage({ id: 'general.back', defaultMessage: 'Back' }),
              icon: 'back',
              action: () => dispatchTyped(SET_EXPORT_VIEW(false)),
            },
          ]}
          rightButtons={[
            {
              label: intl.formatMessage({ id: 'general.download', defaultMessage: 'Download' }),
              icon: 'pdf',
              action: () => generatePDF(''),
              loading: download,
            },
          ]}
        />
        <div className={styles.report}>
          <PdfPreviewExport
            ref={pdfRef}
            skipClasses={[styles.topRightCorner, styles.footer]}
            breakClasses={[styles.subHeader]}
            orientation="landscape"
          >
            <div className={styles.visitPage}>
              <div className={styles.logoDiv}>
                <div>
                  {edit ? (
                    <div className={styles.topRightCornerInput}>
                      <ImageUploader
                        maxWidth={2000}
                        maxHeight={400}
                        image={clinic.logo}
                        label={intl.formatMessage({
                          id: 'general.upload',
                          defaultMessage: 'Upload',
                        })}
                        onUpload={(image) => {
                          dispatch(updateClinic({ ...clinic, logo: image }));
                        }}
                      />
                    </div>
                  ) : (
                    clinic.logo && <img src={clinic.logo} alt="" />
                  )}
                  <div className={styles.logoAdress}>
                    {`${clinic?.name}, ${clinic?.address?.city}, ${clinic?.address?.postcode}, ${clinicCountry}`}
                  </div>
                </div>
              </div>
              <EditableExportTitle editModeInExportPage={edit} />
              <PatientInfoWrapper>
                <div className="patientInfoDiv">
                  <div>
                    <span className={'formated-message'}>
                      <FormattedMessage id="general.patient" defaultMessage="Patient" />
                      {': '}
                    </span>
                    <span className={styles.pdfDataFeild}>
                      {patientFirstName && patientFirstName} {patientLastName && patientLastName}
                    </span>
                  </div>
                  <div>
                    <span className={'formated-message'}>
                      <FormattedMessage
                        id="general.treatingDoctor"
                        defaultMessage="Treating doctor"
                      />
                      {': '}
                    </span>
                    <span className={styles.pdfDataFeild}>{treatingDoctor}</span>
                  </div>
                  <div>
                    <span className={'formated-message'}>
                      <FormattedMessage
                        id="general.referringClinician"
                        defaultMessage="Referring Clinician"
                      />
                      {': '}
                    </span>
                    {isEditingPdfData && edit ? (
                      <div className="referringClinicianInputWrapper">
                        <InputItem
                          type="search-select"
                          options={referralNames}
                          onChange={(option: { label: string; value: string }) =>
                            setReferringClinicianUnderEdit({
                              clinician: option.label,
                              id: option.value,
                            })
                          }
                          value={referringClinicianUnderEdit.clinician}
                          footer={referralFooter}
                        />
                      </div>
                    ) : (
                      <span>{referringClinicianUnderEdit.clinician}</span>
                    )}
                  </div>
                  <div>
                    <span className={'formated-message'}>
                      <FormattedMessage id="general.notes" defaultMessage="Notes" />
                      {': '}
                    </span>
                    {isEditingPdfData && edit ? (
                      <div className="noteInputWrapper">
                        <InputItem
                          maxLength={345}
                          type="textarea"
                          value={noteUnderEdit}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setNoteUnderEdit(event.target.value)
                          }
                        />
                      </div>
                    ) : (
                      <span className={styles.pdfDataFeild}>{noteUnderEdit}</span>
                    )}
                  </div>
                  {edit && (
                    <button
                      onClick={() => {
                        if (isEditingPdfData) {
                          dispatch(SET_NOTE(noteUnderEdit));
                          if (referringClinician?.id !== referringClinicianUnderEdit?.id) {
                            dispatch(
                              updatePatient(
                                userGroupId,
                                patient.id,
                                null,
                                null,
                                null,
                                null,
                                null,
                                null,
                                null,
                                referringClinicianUnderEdit.id,
                                null
                              )
                            );
                          }
                        }
                        setIsEditingPdfData(!isEditingPdfData);
                      }}
                      className={'button'}
                    >
                      <i className={'icon'}>{isEditingPdfData ? 'done' : 'draw'}</i>
                    </button>
                  )}
                </div>
              </PatientInfoWrapper>
              {/* {Timeline div} */}
              <div>
                <PdfProcedureTimeLine />
              </div>
            </div>
            {/* {Step pages} */}
            {procedure.visits.map((visit: Visit) => {
              return getPairedStepsArray(visit.procedureStepStates).map(
                (stepPair, index: number) => {
                  const [firstStep, secondStep] = [stepPair[0], stepPair[1]];
                  return (
                    <div className={styles.visitPage} key={index}>
                      <div className={styles.visitPageHeader}>
                        <span>{visit.name}</span>
                      </div>
                      {getStepHtml(firstStep, true)}
                      {secondStep !== undefined && getStepHtml(secondStep, false)}
                    </div>
                  );
                }
              );
            })}
            <div className={styles.footer}>
              <img src={osstellLogo} alt="logo" />
            </div>
          </PdfPreviewExport>
        </div>
      </div>
      {showModal && (
        <StyledPrompt ref={promptRef} xOffset={'0px'}>
          <InputGroup>
            <InputItem
              label={intl.formatMessage({
                id: 'general.email',
                defaultMessage: 'Email',
              })}
              type="text"
              value={user.email}
              placeholder={'none'}
              noPadding
              onWhite
            />
          </InputGroup>
          <section>
            <Button
              loading={!edit}
              disabled={!edit}
              centered
              primary={true}
              full
              value={intl.formatMessage({
                id: 'general.send',
                defaultMessage: 'Send',
              })}
              onClick={() => generatePDF(user.email)}
            />
          </section>
        </StyledPrompt>
      )}
    </ThemeProvider>
  );
};

const PdfProcedureTimeLine: React.FC = () => {
  const [pages, setPages] = useState<ReactNode[]>();
  const pagesRef = useRef<HTMLDivElement>(null);
  const minScale = 0.1;

  const split = () => {
    if (!pagesRef.current) return;

    const newPages: ReactNode[] = [];
    const el = pagesRef.current;
    if (pagesRef.current.parentElement?.parentElement) {
      const maxWidth = Number.parseInt(
        getComputedStyle(pagesRef.current.parentElement.parentElement).width
      );
      const width = Number.parseInt(getComputedStyle(el).width);
      const height = Number.parseInt(getComputedStyle(el).height);
      const splits = Math.ceil((width * minScale) / maxWidth);
      for (let i = 0; i < splits; i++) {
        newPages.push(
          <ScaleContentToFit key={`PdfProcedureTimeLine_${i}`} minSize={minScale}>
            <div
              style={{
                width: width,
                left: -((maxWidth / (minScale * 100)) * 100) * i,
                position: 'absolute',
                height: height,
                top: 0,
                bottom: 0,
              }}
            >
              <ProcedureTimeLine disabled maxWidth={maxWidth} />
            </div>
            <div
              style={{
                height: height,
                width: width,
              }}
            ></div>
          </ScaleContentToFit>
        );
      }

      setPages(() => newPages);
    }
  };
  return (
    <TimeLineWrapper>
      <div style={{ height: 0, overflow: 'hidden' }}>
        <ScaleContentToFit minSize={minScale} onResize={() => requestAnimationFrame(split)}>
          <div ref={pagesRef}>
            <ProcedureTimeLine disabled maxWidth={1000} />
          </div>
        </ScaleContentToFit>
      </div>
      {pages}
    </TimeLineWrapper>
  );
};

const TimeLineWrapper = styled.div`
  width: 95%;
  margin: 10px auto;
`;

const PatientInfoWrapper = styled.div`
  .patientInfoDiv {
    word-wrap: break-word;
    height: 250px;
    position: relative;
    width: 350px;
    margin: 50px auto 0px auto;
    .formated-message {
      font-weight: bold;
    }
  }
  .referringClinicianInputWrapper {
    display: inline-block;
    word-wrap: normal;
    width: 100%;
    box-shadow: 0px 0px 10px 0 rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    div {
      margin: 0px;
    }
  }

  .noteInputWrapper {
    textarea {
      height: 120px;
    }
    div {
      margin: 0px;
      border-radius: 8px;
      div {
        box-shadow: 0px 0px 10px 0 rgba(0, 0, 0, 0.15);
      }
    }
  }

  .button {
    position: absolute;
    right: -59px;
    top: 0px;
    width: 3rem;
    height: 3rem;
    margin: 0 0 0 -3.75rem;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #edeef7;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    color: var(--color-purple);
    .icon {
      font-size: 1.625rem;
      padding-top: 3px;
    }
    &:focus {
      outline: none;
    }
  }
`;

export default ConsultationExportPage;

const osstellLogo =
  'data:image/png;base64,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';
